import { agreementApi, employeeNotesApi } from 'api';
import { AgreementEntity, UpdateNoteDto } from 'api/generated';
import CryptoJS from 'crypto-js';
import { TFunction } from 'i18next';
import toast from 'react-hot-toast';

export const encryptData = (dataInput: any, encryptionKey: string): string | undefined => {
    try {
        const data = String(dataInput)
        const encryptedData = CryptoJS.AES.encrypt(data, encryptionKey).toString();
        return encryptedData;
    } catch (error) {
        console.error('Error encrypting data:', error);
    }
}

const fileToBase64 = async (file: File) => {
    
    const buffer = await file.arrayBuffer();
    return btoa(
        new Uint8Array(buffer)
          .reduce((data, byte) => data + String.fromCharCode(byte), '')
      );
}

const base64ToFile = (base64: string, fileName: string, type: string) => {
    const binary = atob(base64);
    const byteArray = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        byteArray[i] = binary.charCodeAt(i);
    }
    return new File([byteArray], fileName, { type });
}

export const encryptFile = async (file: File, encryptionKey: string) => {
    const base64 = await fileToBase64(file);
    const enc = encryptData(base64, encryptionKey) ?? '';
    return base64ToFile(enc, file.name, file.type);
}

export const decryptFile = async (file: File, decryptionKey: string) => {
    const enc = await fileToBase64(file);
    const dec = decryptData(enc, decryptionKey);
    return base64ToFile(dec, file.name, file.type);
}

export const encryptAllNotes = async (t: TFunction) => {

    const encryptionKey = localStorage.getItem('encryptionKey');

    if (!encryptionKey)
        return;

    const notes = (await employeeNotesApi.findAll()).data;

    const unencryptedNotes = notes.filter(note => note.text);

    for (const note of unencryptedNotes) {
        const textEncrypted = encryptData(note.text, encryptionKey);
        if (!textEncrypted) {
            toast.error(t('encryption.alerts.agreements.encrypt.error'));
            return
        }

        const noteDto: UpdateNoteDto = {
            text: "",
            text_encrypted: textEncrypted
        }
        await employeeNotesApi.update(note.id, noteDto);
    }

    toast.success(t('encryption.alerts.notes.encrypt.success'));
}

export const decryptData = (encryptedData: string, decryptionKey: string): string => {
    try {
        const decryptedData = CryptoJS.AES.decrypt(encryptedData, decryptionKey).toString(CryptoJS.enc.Utf8);
        return decryptedData;
    } catch (error) {
        console.error('Error decrypting data:', error);
        return '';
    }
}

export const getDecryptedSpecialDailyNet = (specialNet: any, encryptionKey: string): number => {
    // Prefer encrypted value if available and encryption key is present
    if (specialNet.net_encrypted && encryptionKey && encryptionKey !== '') {
        const decryptedValue = decryptData(specialNet.net_encrypted, encryptionKey);
        if (decryptedValue && decryptedValue !== '') {
            return Number(decryptedValue);
        }
    }
    
    // Fall back to cleartext value
    return specialNet.net || 0;
}


export const encryptAllAgrements = async (t: TFunction, isDeleteAll?: boolean): Promise<void> => {
    agreementApi.findAll().then(async ({ data }) => {
        const agreements: AgreementEntity[] = data;
        const encryptionKey = localStorage.getItem('encryptionKey');
        const unencryptedAgreements = agreements.filter((agreement) => agreement.net_daily_encrypted === null);
        if (isDeleteAll) {
            const actions = unencryptedAgreements.map(async (agreement) => {
                return agreementApi.remove(String(agreement.id));
            });
            await Promise.all(actions).then(() => {
                toast.success(t('encryption.alerts.agreements.delete.success'));
            }).catch(() => {
                toast.error(t('encryption.alerts.agreements.delete.error'));
            });
            window.location.hash = "";
            history.replaceState(null, document.title, window.location.pathname + window.location.search);
            return;
        }

        if (encryptionKey) {
            const actions = unencryptedAgreements.map(async (agreement) => {

                const netDailyEncrypted = encryptData(agreement.net_daily, encryptionKey);

                if (!netDailyEncrypted) {
                    toast.error(t('encryption.alerts.agreements.encrypt.error'));
                    return;
                }

                const updatedData = {
                    ...agreement,
                    net_daily: 0,
                    endedOn: agreement.endedOn ?? undefined,
                    notes: agreement.notes ?? undefined,
                    downpayment: agreement.downpayment ?? undefined,
                    net_daily_encrypted: netDailyEncrypted,
                    hoursVacation: agreement.hoursVacation ?? undefined,
                    daysVacation: agreement.daysVacation ?? undefined,
                    weeklyWorkHours: agreement.weeklyWorkHours ?? undefined,
                    balance730: agreement.balance730 ?? undefined,
                    balance730_encrypted: agreement.balance730_encrypted ?? undefined,
                    balance730_date: agreement.balance730_date ?? undefined,
                    costPerHour: (agreement as any).costPerHour ?? undefined,
                    costPerHour_encrypted: (agreement as any).costPerHour_encrypted ?? undefined,
                };
                return agreementApi.update(String(agreement.id), updatedData);
            });
            await Promise.all(actions).then(() => {
                toast.success(t('encryption.alerts.agreements.encrypt.success'));
            }).catch(() => {
                toast.error(t('encryption.alerts.agreements.encrypt.error'))
            });
            window.location.hash = "";
            history.replaceState(null, document.title, window.location.pathname + window.location.search);
        }
    });
}